import React, {useState} from "react";
import {Autocomplete, Box, IconButton, ListItemText, Popover, PopperProps, TextField, Tooltip} from "@mui/material";
import {Add, AddRounded} from "@mui/icons-material";
import {sendAiChatPrompt} from "../../services/aiChatService.ts";
import {useCase} from "../../provider/CaseProvider.tsx";
import {useAiChat} from "../../provider/AiChatProvider.tsx";
import {createResource} from "../../services/ReactQuery/reactQueryService.ts";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useAiEventStreamRegistry} from "../../provider/AiEventStreamProvider.tsx";

// Source of truth for slash skills is the Legalbird AI app (pdf_forms/definitions.py and the
// workflow skill markdown files); new skills must be mirrored here manually. Intentionally
// hardcoded: replace with an API call once we have more than 10 skills.
const AI_CHAT_SKILLS = [
  {
    command: "/vollstreckungsauftrag",
    label: "Vollstreckungsauftrag erstellen",
    description: "Vollstreckungsauftrag als PDF aus den Falldaten erstellen.",
    agents: ["LIA", "LEXA"],
  },
  {
    command: "/fristen",
    label: "Fristen notieren",
    description: "Haupt- und Vorfristen als Aktivitäten am Fall notieren.",
    agents: ["LIA", "LEXA"],
  },
  {
    command: "/verjährung",
    label: "Verjährung prüfen",
    description: "Verjährung von Ansprüchen prüfen und Fristaktivitäten vorbereiten.",
    agents: ["LEXA"],
  },
  {
    command: "/anspruchsgrundlagen",
    label: "Anspruchsgrundlagen prüfen",
    description: "Fallprüfung: Anspruchsgrundlagen im Vertragsrecht prüfen.",
    agents: ["LEXA"],
  },
];

export default function AiChatSkills() {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const { product } = useCase();
  const { activeThreadId, setActiveThreadId, activeAgent, activeEditStepId, setIsCreatingThread, isLoading } =
    useAiChat();
  const createMutation = useMutation({
    mutationFn: createResource,
  });
  const queryClient = useQueryClient();
  const { getStore } = useAiEventStreamRegistry();

  if (!product) {
    return null;
  }

  const handleMenuItemClick = async (command: string) => {
    setAnchorEl(null);
    await sendAiChatPrompt({
      product,
      activeThreadId,
      setActiveThreadId,
      createMutation,
      queryClient,
      getStreamStore: getStore,
      prompt: command,
      setIsCreatingThread,
      agentType: activeAgent,
      activeExecutionStepId: activeEditStepId,
    });
  };

  return (
    <>
      <Tooltip title={isLoading ? "Zum Auswählen einer Aktion zuerst die Eingabe leeren" : null}>
      <IconButton onClick={(event) => setAnchorEl(event.currentTarget)}>
        <AddRounded />
      </IconButton>
      </Tooltip>
      <Popover
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorEl={anchorEl}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        disableScrollLock
      >
        <Autocomplete
          open
          options={AI_CHAT_SKILLS.filter((aiSkill) => aiSkill.agents.includes(activeAgent))}
          slots={{ popper: InlineAutocompletePopper }}
          sx={{ width: "22rem", margin: "0 auto" }}
          forcePopupIcon={false}
          slotProps={{
            paper: {
              elevation: 0,
            },
          }}
          renderInput={(params) => <TextField {...params} label="Aktion suchen" />}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;
            return (
              <Box component={"li"} key={key} {...optionProps}>
                <ListItemText
                  primary={<strong>{option.label}</strong>}
                  secondary={option.description}
                  onClick={() => handleMenuItemClick(option.command)}
                  slotProps={{
                    primary: { variant: "body2" },
                    secondary: { variant: "caption" },
                  }}
                />
              </Box>
            );
          }}
        />
      </Popover>
    </>
  );
}

function InlineAutocompletePopper({ children, open, ref }: PopperProps) {
  if (!open) {
    return null;
  }
  const content = typeof children === "function" ? children({ placement: "bottom" }) : children;
  return <Box ref={ref}>{content}</Box>;
}
